import { cn } from "@/shared/lib/utils"
import { composeEventHandlers } from "@/shared/lib/utils/compose-event-handlers"
import { WebApp } from "@/shared/telegram"

import styles from "./link.module.css"
import { isAriaDisabled, isTelegramHost, parseOpenableUrl } from "./telegram-link-utils"

interface TelegramLinkOptions {
  /**
   * Render the page with Telegram's Instant View reader when one exists for it.
   * Bot API 6.4+; older clients ignore it and show the page as it is.
   * Not a choice of browser: that is the user's "open links in" setting, which no Mini App API overrides.
   */
  tryInstantView?: boolean
}

interface TelegramLinkProps extends React.ComponentProps<"a"> {
  href: string
  /** Anchors have no native disabled state: this sets `aria-disabled` and refuses the click, as does passing `aria-disabled` itself. */
  disabled?: boolean
  /** Passed on to `openLink` only; a `t.me` destination goes to `openTelegramLink`, which opens no page. */
  options?: TelegramLinkOptions
}

/**
 * A link that opens through Telegram rather than through the webview.
 *
 * Carries no look beyond the focus ring and the disabled state, so call sites style it freely.
 *
 * An unsafe or unparseable URL yields no `href` and an `aria-disabled` anchor, so a `javascript:`
 * payload cannot be smuggled through a link the user sees as ordinary.
 *
 * `t.me` goes to `openTelegramLink`, anything else to `openLink`: `openTelegramLink` throws
 * `WebAppTgUrlInvalid` on a foreign host before it even checks the client version.
 */
export const TelegramLink: React.FC<TelegramLinkProps> = ({
  href,
  options,
  disabled = false,
  className,
  target = "_blank",
  rel = "noopener noreferrer",
  onClick,
  children,
  "aria-disabled": ariaDisabled,
  ...props
}) => {
  const url = parseOpenableUrl(href)
  // `aria-disabled` announces the link as inert without making it so, hence the click guard below.
  const isDisabled = disabled || isAriaDisabled(ariaDisabled)
  const isInert = isDisabled || url === null

  // Runs first, like a disabled button: cancelling here keeps the caller's handler and the opening from running.
  const refusePress: React.MouseEventHandler<HTMLAnchorElement> = event => {
    if (isDisabled) event.preventDefault()
  }

  /* No keydown handler: Enter activates an anchor natively and arrives here as a click, and Space is not an
     anchor activation key. */
  const openInTelegram: React.MouseEventHandler<HTMLAnchorElement> = event => {
    event.preventDefault()

    if (url === null) {
      // A refused link looks ordinary and does nothing on press; without this the only trace is a missing href.
      console.warn("[TelegramLink] Refusing to open a URL that is not http(s):", href)
      return
    }

    if (isTelegramHost(url)) {
      WebApp.openTelegramLink(href)
      return
    }

    WebApp.openLink(
      href,
      options?.tryInstantView === undefined ? undefined : { try_instant_view: options.tryInstantView }
    )
  }

  return (
    <a
      {...props}
      className={cn(styles["telegram-link"], className)}
      href={isInert ? undefined : href}
      target={target}
      rel={rel}
      aria-disabled={isInert ? true : undefined}
      onClick={composeEventHandlers(refusePress, composeEventHandlers(onClick, openInTelegram))}>
      {children}
    </a>
  )
}
